<template>
    <section
      ref="sectionRef"
      class="notification-banner"
      :class="{
        'notification-banner--visible': isVisible
      }"
    >
      <div class="notification-banner__inner">
        <!-- Header -->
        <div class="notification-banner__header">
          <span class="notification-banner__eyebrow">
            Всегда на связи
          </span>
  
          <h2 class="notification-banner__title">
            Быстрый коннект
            <span>с пользователями!</span>
          </h2>
  
          <p class="notification-banner__description">
            Пользователи могут подписываться на интересующие компании
            и первыми получать push-уведомления о новых вакансиях.
            А персональные подборки помогают вашим предложениям
            находить подходящих кандидатов даже тогда, когда они
            не заходят в сервис каждый день.
          </p>
        </div>
  
        <!-- Visual -->
        <div class="notification-banner__visual">
          <div class="ambient ambient--left"></div>
          <div class="ambient ambient--right"></div>
  
          <img
            :src="currentImage"
            class="notification-banner__image"
            alt="Уведомления HR3"
            draggable="false"
          />

          <div class="notification-analysis">
            <span class="notification-analysis__label">
                Умные уведомления
            </span>

            <h2 class="notification-analysis__title">
                Вакансии сами найдут своих кандидатов
            </h2>

            <div class="notification-analysis__content">
                <p>
                Пользователи могут подписываться на компании,
                которые им интересны, и узнавать о новых
                возможностях одними из первых.
                </p>

                <p>
                Как только компания публикует новую вакансию,
                подписчики получают push-уведомление и могут
                сразу перейти к просмотру и откликнуться.
                </p>

                <p>
                Каждую неделю HR3 также формирует
                персональную подборку вакансий, ориентируясь
                на интересы, профессиональное направление
                и предпочтения пользователя.
                </p>

                <p class="notification-analysis__result">
                Так ваши вакансии получают дополнительный охват
                среди заинтересованных кандидатов, а подходящие
                специалисты не пропускают новые возможности.
                </p>
            </div>
            </div>

        </div>
      </div>
    </section>
  
    <div class="separator"></div>
  </template>
  
  <script setup>
  import {
    computed,
    onMounted,
    onUnmounted,
    ref
  } from "vue";
  
  const lightImage = new URL(
    "../../assets/NotificationBanner-light.png",
    import.meta.url
  ).href;
  
  const darkImage = new URL(
    "../../assets/NotificationBanner-dark.png",
    import.meta.url
  ).href;
  
  const sectionRef = ref(null);
  const isVisible = ref(false);
  
  const theme = ref(
    document.documentElement.getAttribute(
      "data-theme"
    ) || "light"
  );
  
  const currentImage = computed(() => {
    return theme.value === "dark"
      ? darkImage
      : lightImage;
  });
  
  let themeObserver = null;
  let intersectionObserver = null;
  
  onMounted(() => {
    themeObserver = new MutationObserver(() => {
      theme.value =
        document.documentElement.getAttribute(
          "data-theme"
        ) || "light";
    });
  
    themeObserver.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "data-theme"
        ]
      }
    );
  
    intersectionObserver =
      new IntersectionObserver(
        entries => {
          const entry = entries[0];
  
          if (!entry?.isIntersecting) {
            return;
          }
  
          isVisible.value = true;
          intersectionObserver?.disconnect();
        },
        {
          threshold: 0.12
        }
      );
  
    if (sectionRef.value) {
      intersectionObserver.observe(
        sectionRef.value
      );
    }
  });
  
  onUnmounted(() => {
    themeObserver?.disconnect();
    intersectionObserver?.disconnect();
  });
  </script>
  
  <style scoped>
  * {
    box-sizing: border-box;
  }
  
  .notification-banner {
    position: relative;
  
    width: 100%;
    overflow: hidden;
  
    background: var(--background);
  
    opacity: 0;
    transform: translateY(18px);
  
    transition:
      opacity 0.75s ease,
      transform 0.75s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        );
  }
  
  .notification-banner--visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  .notification-banner__inner {
    width: 100%;
    max-width: 1440px;
  
    margin: 0 auto;
  
    padding:
      78px
      64px
      0px;
  }
  
  /* Header */
  
  .notification-banner__header {
    width: 100%;
    max-width: 820px;
  
    margin-bottom: 28px;
  }
  
  .notification-banner__eyebrow {
    display: inline-flex;
    align-items: center;
  
    margin-bottom: 14px;
  
    color: #2277e6;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 11px;
    line-height: 1;
  
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  
  .notification-banner__title {
    margin:
      0
      0
      17px;
  
    color: var(--text);
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size:
      clamp(
        42px,
        4.3vw,
        64px
      );
  
    line-height: 0.98;
    letter-spacing: -0.045em;
  }
  
  .notification-banner__title span {
    color: #2277e6;
  }
  
  .notification-banner__description {
    max-width: 760px;
  
    margin: 0;
  
    color:
      var(
        --text-secondary,
        #6f7682
      );
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 16px;
    line-height: 1.58;
    letter-spacing: -0.015em;
  }
  
  /* Visual */
  
  .notification-banner__visual {
  position: relative;

  width: 100%;

  display: flex;
  align-items: flex-end;
  justify-content: center;

  isolation: isolate;
}
  
  .notification-banner__image {
    position: relative;
    z-index: 2;
  
    width: min(
      900px,
      100%
    );
  
    height: auto;
  
    display: block;
  
    object-fit: contain;
  
    user-select: none;
  
    opacity: 0;
  
    transform:
      translateY(16px)
      scale(0.985);
  
    filter:
      drop-shadow(
        0
        28px
        54px
        rgba(
          32,
          67,
          120,
          0.07
        )
      );
  
    transition:
      opacity 0.9s ease 0.12s,
      transform 0.9s
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        )
        0.12s;
  }
  
  .notification-banner--visible
  .notification-banner__image {
    opacity: 1;
  
    transform:
      translateY(0)
      scale(1);
  }
  
  /* Ambient */
  
  .ambient {
    position: absolute;
    z-index: 0;
  
    border-radius: 50%;
  
    pointer-events: none;
  
    filter: blur(110px);
  
    opacity: 0.11;
  
    animation:
      notificationAmbient
      16s
      ease-in-out
      infinite;
  }
  
  .ambient--left {
    width: 340px;
    height: 340px;
  
    left: 12%;
    top: 10%;
  
    background: #4b8ff7;
  }
  
  .ambient--right {
    width: 280px;
    height: 280px;
  
    right: 13%;
    bottom: 8%;
  
    background: #7865ef;
  
    animation-delay: -7s;
  }
  
  @keyframes notificationAmbient {
    0%,
    100% {
      transform:
        translate3d(
          0,
          0,
          0
        )
        scale(1);
    }
  
    50% {
      transform:
        translate3d(
          18px,
          -14px,
          0
        )
        scale(1.05);
    }
  }
  
  /* Separator */
  
  .separator {
    width: 100%;
    height: 1px;
  
    background:
      var(
        --separator_home,
        rgba(
          0,
          0,
          0,
          0.08
        )
      );
  }
  
  /* Dark */
  
  html[data-theme="dark"]
  .notification-banner__description {
    color: #9ba2ae;
  }
  
  html[data-theme="dark"]
  .notification-banner__image {
    filter:
      drop-shadow(
        0
        28px
        58px
        rgba(
          0,
          0,
          0,
          0.22
        )
      );
  }
  
  html[data-theme="dark"]
  .ambient {
    opacity: 0.16;
  }
  
  /* Laptop */
  
  @media (max-width: 1366px) {
    .notification-banner__inner {
      padding:
        56px
        48px
        38px;
    }
  
    .notification-banner__header {
      margin-bottom: 18px;
    }
  
    .notification-banner__title {
      font-size:
        clamp(
          38px,
          4vw,
          54px
        );
    }
  
    .notification-banner__description {
      max-width: 700px;
  
      font-size: 14px;
    }
  
    .notification-banner__visual {
      min-height: 500px;
    }
  
    .notification-banner__image {
      width: min(
        800px,
        100%
      );
    }
  }
  
  /* 13" laptop */
  
  @media (
    min-width: 769px
  ) and (
    max-height: 820px
  ) {
    .notification-banner__inner {
      padding:
        40px
        46px
        25px;
    }
  
    .notification-banner__header {
      margin-bottom: 12px;
    }
  
    .notification-banner__eyebrow {
      margin-bottom: 9px;
  
      font-size: 9px;
    }
  
    .notification-banner__title {
      margin-bottom: 11px;
  
      font-size:
        clamp(
          34px,
          3.6vw,
          48px
        );
    }
  
    .notification-banner__description {
      max-width: 670px;
  
      font-size: 12px;
      line-height: 1.45;
    }
  
    .notification-banner__visual {
      min-height: 430px;
    }
  
    .notification-banner__image {
      width: min(
        720px,
        100%
      );
    }
  }
  
  /* Tablet */
  
  @media (max-width: 900px) {
    .notification-banner__inner {
      padding:
        48px
        28px
        32px;
    }
  
    .notification-banner__title {
      font-size:
        clamp(
          36px,
          7vw,
          50px
        );
    }
  
    .notification-banner__visual {
      min-height: 0;
    }
  
    .notification-banner__image {
      width: 90%;
    }
  }
  
  /* Mobile */
  
  @media (max-width: 768px) {
    .notification-banner__inner {
      padding:
        38px
        18px
        26px;
    }
  
    .notification-banner__header {
      margin-bottom: 24px;
    }
  
    .notification-banner__eyebrow {
      margin-bottom: 10px;
  
      font-size: 9px;
    }
  
    .notification-banner__title {
      margin-bottom: 14px;
  
      font-size: 35px;
      line-height: 1.03;
    }
  
    .notification-banner__description {
      font-size: 13px;
      line-height: 1.5;
    }
  
    .notification-banner__visual {
      margin:
        0
        -10px;
    }
  
    .notification-banner__image {
      width: 108%;
  
      filter: none;
    }
  
    .ambient {
      display: none;
    }
  }
  
  @media (max-width: 480px) {
    .notification-banner__inner {
      padding:
        32px
        16px
        22px;
    }
  
    .notification-banner__title {
      font-size: 30px;
    }
  
    .notification-banner__description {
      font-size: 12px;
    }
  
    .notification-banner__image {
      width: 116%;
    }
  }
  
  /* Reduced motion */
  
  @media (
    prefers-reduced-motion:
    reduce
  ) {
    .notification-banner,
    .notification-banner__image,
    .ambient {
      animation: none;
      transition: none;
    }
  
    .notification-banner {
      opacity: 1;
      transform: none;
    }
  
    .notification-banner__image {
      opacity: 1;
      transform: none;
    }
  }

  /* Notification analysis */

.notification-analysis {
  position: absolute;
  z-index: 5;

  top: 180px;
  right: 2%;

  width: 32%;
  max-width: 410px;

  padding: 22px 24px;

  transform: translateY(-44%);

  color: #25282e;

  background:
    rgba(
      255,
      255,
      255,
      0.74
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.72
    );

  border-radius: 20px;

  box-shadow:
    0 18px 50px
    rgba(
      30,
      50,
      80,
      0.07
    );

  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  opacity: 0;

  transition:
    opacity 0.8s ease 0.3s,
    transform 0.8s
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      0.3s;
}

.notification-banner--visible
.notification-analysis {
  opacity: 1;

  transform: translateY(-50%);
}

.notification-analysis__label {
  display: inline-flex;
  align-items: center;

  margin-bottom: 11px;
  padding: 5px 8px;

  color: #2277e6;

  background:
    rgba(
      34,
      119,
      230,
      0.08
    );

  border:
    1px solid
    rgba(
      34,
      119,
      230,
      0.1
    );

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
  line-height: 1;

  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.notification-analysis__title {
  margin:
    0
    0
    13px;

  color: #20242c;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 20px;
  line-height: 1.15;
  letter-spacing: -0.025em;
}

.notification-analysis__content {
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.notification-analysis__content p {
  margin: 0;

  color: #626a77;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;
  line-height: 1.48;
}

.notification-analysis__result {
  padding:
    11px
    12px !important;

  color:
    #395a88 !important;

  background:
    linear-gradient(
      135deg,
      rgba(
        34,
        119,
        230,
        0.07
      ),
      rgba(
        83,
        104,
        232,
        0.045
      )
    );

  border:
    1px solid
    rgba(
      34,
      119,
      230,
      0.08
    );

  border-radius: 11px;
}

/* Dark */

html[data-theme="dark"]
.notification-analysis {
  color: #ffffff;

  background:
    rgba(
      28,
      28,
      30,
      0.78
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0 20px 55px
    rgba(
      0,
      0,
      0,
      0.22
    );
}

html[data-theme="dark"]
.notification-analysis__label {
  color: #72a8ff;

  background:
    rgba(
      63,
      137,
      241,
      0.12
    );

  border-color:
    rgba(
      74,
      145,
      246,
      0.16
    );
}

html[data-theme="dark"]
.notification-analysis__title {
  color: #f2f3f5;
}

html[data-theme="dark"]
.notification-analysis__content p {
  color: #a7adb7;
}

html[data-theme="dark"]
.notification-analysis__result {
  color:
    #9fc3ff !important;

  background:
    linear-gradient(
      135deg,
      rgba(
        40,
        135,
        247,
        0.12
      ),
      rgba(
        83,
        104,
        232,
        0.07
      )
    );

  border-color:
    rgba(
      74,
      145,
      246,
      0.14
    );
}

/* Laptop */

@media (max-width: 1366px) {
  .notification-analysis {
    right: 0;

    width: 33%;
    max-width: 355px;

    padding: 17px 18px;

    border-radius: 16px;
  }

  .notification-analysis__label {
    margin-bottom: 8px;

    font-size: 8px;
  }

  .notification-analysis__title {
    margin-bottom: 10px;

    font-size: 17px;
  }

  .notification-analysis__content {
    gap: 8px;
  }

  .notification-analysis__content p {
    font-size: 9.5px;
    line-height: 1.42;
  }

  .notification-analysis__result {
    padding:
      8px
      9px !important;
  }
}

/* 13" laptop */

@media (
  min-width: 769px
) and (
  max-height: 820px
) {
  .notification-analysis {
    width: 32%;
    max-width: 325px;

    padding: 14px 16px;
  }

  .notification-analysis__title {
    margin-bottom: 8px;

    font-size: 15px;
  }

  .notification-analysis__content {
    gap: 6px;
  }

  .notification-analysis__content p {
    font-size: 8.5px;
    line-height: 1.38;
  }

  .notification-analysis__result {
    padding:
      7px
      8px !important;
  }
}

/* Tablet / mobile */

@media (max-width: 900px) {
  .notification-banner__visual {
    flex-direction: column;
  }

  .notification-analysis {
    position: relative;

    top: auto;
    right: auto;

    width: 100%;
    max-width: 680px;

    margin-top: 16px;
    padding: 18px 20px;

    transform: translateY(10px);
  }

  .notification-banner--visible
  .notification-analysis {
    transform: translateY(0);
  }

  .notification-analysis__title {
    font-size: 18px;
  }

  .notification-analysis__content p {
    font-size: 11px;
  }
}

  </style>